/**
 * AudioPlayer - Player de áudio customizado para mensagens de chat
 * Baseado nos padrões de WhatsApp/Telegram
 */

import React, { useState, useRef, useEffect } from 'react';
import { Icon } from '@/shared/icons';
import { api } from '@/lib/api';
import { pauseOtherMedia } from '../utils/mediaPlayback';
interface AudioPlayerProps {
  src: string;
  className?: string;
  isOwn?: boolean;
}

// Alturas determinísticas das barras da waveform (.wave) — calculadas 1x, nunca no render (evita flicker)
const WAVE_BARS: number[] = Array.from({ length: 22 }, (_, i) => 20 + Math.round(30 * Math.abs(Math.sin(i * 1.7))));

export const AudioPlayer: React.FC<AudioPlayerProps> = ({ src, className = '', isOwn = false }) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    const handlePlay = () => {
      setIsPlaying(true);
      // Pausa qualquer outra mídia (áudio ou vídeo) que esteja tocando — apenas uma por vez
      pauseOtherMedia(audio);
    };

    const handlePause = () => {
      setIsPlaying(false);
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
    };
  }, []);

  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;

    // O estado isPlaying é sincronizado pelos eventos 'play'/'pause' do elemento
    if (audio.paused) {
      audio.play();
    } else {
      audio.pause();
    }
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current;
    if (!audio) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const newTime = percentage * duration;

    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const cyclePlaybackRate = () => {
    const rates = [1, 1.5, 2];
    const currentIndex = rates.indexOf(playbackRate);
    const nextRate = rates[(currentIndex + 1) % rates.length];

    setPlaybackRate(nextRate);
    if (audioRef.current) {
      audioRef.current.playbackRate = nextRate;
    }
  };

  const handleDownload = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (isDownloading) return;

    const filename = src.split('/').pop()?.split('?')[0] || 'audio.ogg';

    try {
      setIsDownloading(true);
      const response = await api.get('conversation-messages/download', {
        params: { url: src, filename },
        responseType: 'blob',
      });

      const blob = response.data instanceof Blob ? response.data : new Blob([response.data]);
      const objectUrl = URL.createObjectURL(blob);

      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);

      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      console.error('Falha ao baixar áudio:', err);
    } finally {
      setIsDownloading(false);
    }
  };

  const formatTime = (time: number): string => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  // Player sem container próprio (.maud): wave/dur/spd herdam a cor do balão via currentColor.
  // Play (.pl): inbound = primary/#fff, outbound (isOwn) = #fff/primary.
  const playBg = isOwn ? '#ffffff' : 'var(--theme-primary, #6366f1)';
  const playColor = isOwn ? 'var(--theme-primary, #6366f1)' : '#ffffff';

  return (
    <div className={`flex items-center gap-[9px] min-w-[220px] py-0.5 ${className}`}>
      <audio ref={audioRef} src={src} preload="metadata" />

      {/* Play/Pause (.pl — círculo 34px) */}
      <button
        type="button"
        onClick={togglePlayPause}
        className="w-[34px] h-[34px] flex-none rounded-full flex items-center justify-center transition-colors"
        style={{ background: playBg, color: playColor, border: 0 }}
        aria-label={isPlaying ? 'Pause' : 'Play'}
        data-testid="audio-player-play"
      >
        {isPlaying ? (
          <Icon name="pause" className="w-4 h-4" />
        ) : (
          <Icon name="play" className="w-4 h-4 ml-0.5" />
        )}
      </button>

      {/* Waveform (.wave — barras finas; clique navega) */}
      <div
        className="flex-1 flex items-center gap-0.5 h-[26px] cursor-pointer"
        onClick={handleProgressClick}
      >
        {WAVE_BARS.map((height, i) => {
          const isFilled = (i / WAVE_BARS.length) * 100 <= progress;
          return (
            <i
              key={i}
              className="flex-1 rounded-[2px]"
              style={{ background: 'currentColor', opacity: isFilled ? 0.85 : 0.4, height: `${height}%` }}
            />
          );
        })}
      </div>

      {/* Duração (.dur) */}
      <span className="flex-none tabular-nums" style={{ fontSize: 11, opacity: 0.8 }}>
        {formatTime(isPlaying || currentTime > 0 ? currentTime : duration)}
      </span>

      {/* Velocidade (.spd — pill com borda) */}
      <button
        type="button"
        onClick={cyclePlaybackRate}
        className="flex-none rounded-[5px]"
        style={{ fontSize: 10, fontWeight: 700, border: '1px solid currentColor', padding: '1px 4px', opacity: 0.8, background: 'transparent', color: 'currentColor' }}
        aria-label="Playback speed"
        data-testid="audio-player-speed"
      >
        {playbackRate}x
      </button>

      {/* Download (feature real preservada; ícone flat sem container) */}
      <button
        type="button"
        onClick={handleDownload}
        disabled={isDownloading}
        className="flex-none flex items-center justify-center transition-opacity disabled:opacity-60"
        style={{ opacity: 0.8, background: 'transparent', border: 0, color: 'currentColor' }}
        aria-label="Baixar áudio"
        data-testid="audio-player-download"
      >
        <Icon name={isDownloading ? 'loader' : 'download'} className={`w-4 h-4 ${isDownloading ? 'animate-spin' : ''}`} />
      </button>
    </div>
  );
};

export default AudioPlayer;
